import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, AlertTriangleIcon, TrashIcon, XCircleIcon, EditIcon } from 'lucide-react';
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'delete' | 'close' | 'edit' | 'warning';
  assessmentTitle?: string;
}
export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  type = 'warning',
  assessmentTitle
}: ConfirmationModalProps) {
  if (!isOpen) return null;
  const getIcon = () => {
    switch (type) {
      case 'delete':
        return <TrashIcon size={24} className="text-red-600" data-id="element-977" />;
      case 'close':
        return <XCircleIcon size={24} className="text-[#FF9D33]" data-id="element-978" />;
      case 'edit':
        return <EditIcon size={24} className="text-[#2563EB]" data-id="element-979" />;
      default:
        return <AlertTriangleIcon size={24} className="text-[#FF9D33]" data-id="element-980" />;
    }
  };
  const getIconBgColor = () => {
    switch (type) {
      case 'delete':
        return 'bg-red-500 bg-opacity-10';
      case 'close':
        return 'bg-[#FF9D33] bg-opacity-10';
      case 'edit':
        return 'bg-[#2563EB] bg-opacity-10';
      default:
        return 'bg-[#FF9D33] bg-opacity-10';
    }
  };
  const getConfirmButtonColor = () => {
    switch (type) {
      case 'delete':
        return 'bg-red-600 hover:bg-red-700';
      case 'close':
        return 'bg-[#FF9D33] hover:bg-[#ff8c1a]';
      case 'edit':
        return 'bg-[#2563EB] hover:bg-[#1d4ed8]';
      default:
        return 'bg-[#FF9D33] hover:bg-[#ff8c1a]';
    }
  };
  return <AnimatePresence data-id="element-981">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-982">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-md w-full border border-custom" onClick={e => e.stopPropagation()} data-id="element-983">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-custom" data-id="element-984">
            <div className="flex items-start gap-4" data-id="element-985">
              <div className={`w-12 h-12 ${getIconBgColor()} rounded-xl flex items-center justify-center flex-shrink-0`} data-id="element-986">
                {getIcon()}
              </div>
              <div className="flex-1" data-id="element-987">
                <h2 className="text-lg font-bold text-text-primary mb-1" data-id="element-988">
                  {title}
                </h2>
                <p className="text-sm text-text-secondary" data-id="element-989">{description}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors flex-shrink-0" aria-label="Close modal" data-id="element-990">
              <XIcon size={20} className="text-text-secondary" data-id="element-991" />
            </button>
          </div>

          {/* Assessment Title (if provided) */}
          {assessmentTitle && <div className="px-6 pt-4" data-id="element-992">
              <div className="bg-surface rounded-lg p-3 border border-custom" data-id="element-993">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1" data-id="element-994">
                  Assessment
                </p>
                <p className="text-sm font-semibold text-text-primary" data-id="element-995">
                  {assessmentTitle}
                </p>
              </div>
            </div>}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6" data-id="element-996">
            <button onClick={onClose} className="px-6 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors" data-id="element-997">
              {cancelLabel}
            </button>
            <button onClick={() => {
            onConfirm();
            onClose();
          }} className={`px-6 py-2.5 text-white rounded-lg font-semibold transition-colors shadow-sm ${getConfirmButtonColor()}`} data-id="element-998">
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}